import { DeleteOutlined } from "@ant-design/icons/lib/icons";
import PlusOutlined from "@ant-design/icons/lib/icons/PlusOutlined";
import { useState } from "@hookstate/core";
import { Switch } from "antd";
import Button from "antd/es/button";
import Input from "antd/es/input";
import PageHeader from "antd/es/page-header";
import Card from "antd/lib/card";
import Form from "antd/lib/form";
import { Col, Row } from "antd/lib/grid";
import Space from "antd/lib/space";
import Spin from "antd/lib/spin";
import Typography from "antd/lib/typography";
import { AxiosError, AxiosResponse } from "axios";
import _ from "lodash";
import React, { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Accessible from "../../../../../@vodea/vodea-ui/components/Accessible";
import VuiContainer from "../../../../../@vodea/vodea-ui/components/Container";
import VuiEditor, {
  vuiEditorBaseInit,
} from "../../../../../@vodea/vodea-ui/components/Editor";
import VuiUploadImages from "../../../../../@vodea/vodea-ui/components/UploadImages";
import {
  ACCESS_PERMISSION,
  env,
  IMAGE_RECOMMENDATION_SIZE,
} from "../../../../../constant";
import {
  handleBackendError,
  openNotification,
  toFileList,
} from "../../../../../functions/global";
import PageRepository from "../../../../../repositories/PageRepository";

const { TextArea } = Input;
const { Text } = Typography;

let title = "Informational Home Page";

const AppPageHomeForm: React.FC<any> = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const pageTitle = id
    ? t("common.text.editItem", { item: title })
    : t("common.text.addNewItem", { item: title });

  // Form
  const [form] = Form.useForm();
  const disable = useState(false);
  const loading = useState(false);

  const onFinish = async (data: any) => {
    loading.set(true);

    const formData = {
      ...data,
      image_about: _.get(data, "image_about[0].url"),
      banners: data?.banners?.map((item: any) => {
        return {
          ...item,
          image: _.get(item, "image[0].url"),
          image_mobile: _.get(item, "image_mobile[0].url"),
        };
      }),
      clients: data?.clients?.map((item: any) => {
        return {
          ...item,
          image: _.get(item, "image[0].url"),
        };
      }),
      offices: data?.offices?.map((item: any) => {
        return {
          ...item,
          image: _.get(item, "image[0].url"),
        };
      }),
      merchandises: data?.merchandises?.map((item: any) => {
        return {
          ...item,
          image: _.get(item, "image[0].url"),
        };
      }),
    };

    PageRepository.createHome(formData)
      .then((res: AxiosResponse) => {
        navigate(-1);
        if (!id) {
          openNotification(
            "success",
            t("notification.success.createItem", { item: title })
          );
        } else {
          openNotification(
            "success",
            t("notification.success.updateItem", { item: title })
          );
          loading.set(false);
        }
      })
      .catch((e: AxiosError) => {
        handleBackendError(e, t("notification.error.default"));
        loading.set(false);
      });
  };

  useEffect(() => {
    if (id) {
      getData();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const getData = async () => {
    await PageRepository.show(id)
      .then((res: AxiosResponse) => {
        const data = res.data?.data || {};

        if (!_.isEmpty(data)) {
          const modified: any = {
            ...data,
          };

          Object.keys(modified).forEach((key) => {
            if (key === "image_about") {
              modified[key] = toFileList({ url: data[key] });
            } else if (
              key === "banners" ||
              key === "clients" ||
              key === "offices" ||
              key === "merchandises"
            ) {
              modified[key] = data[key].map((item: any) => {
                const obj: any = {
                  ...item,
                  image: toFileList({ url: item.image }),
                };
                if (item.image_mobile) {
                  obj["image_mobile"] = toFileList({ url: item.image_mobile });
                }
                return obj;
              });
            }
          });

          form.setFieldsValue({
            ...modified,
          });
        }
      })
      .catch((e: AxiosError) => {
        console.log(e);
      });
  };

  return (
    <>
      <Helmet>
        <title>{title} Detail</title>
      </Helmet>
      <VuiContainer>
        <PageHeader
          className="default-page-header"
          onBack={() => navigate(-1)}
          title={pageTitle}
        />

        <Form
          form={form}
          layout={"vertical"}
          onFinish={onFinish}
          initialValues={{
            banners: [
              {
                title: "",
                description: "",
                image: [],
                image_mobile: [],
                can_click: false,
                link: "",
              },
            ],
            clients: [
              {
                name: "",
                summary: "",
                image: [],
              },
            ],
            offices: [
              {
                description: "",
                image: [],
              },
            ],
            merchandises: [
              {
                name: "",
                description: "",
                image: [],
              },
            ],
          }}
        >
          <Row gutter={[16, 16]}>
            <Col
              className="gutter-row"
              md={{
                span: 16,
              }}
              xs={{
                span: 24,
              }}
            >
              <Card title={""} className="mb16">
                <Space
                  style={{
                    width: "100%",
                  }}
                  direction="vertical"
                  size={0}
                >
                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        name="meta_title"
                        label={"Page Title (SEO)"}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: "Page Title (SEO)",
                            }),
                          },
                        ]}
                      >
                        <Input
                          size={"large"}
                          placeholder={t("common.text.input", {
                            item: "Page Title (SEO)",
                          })}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        name="meta_description"
                        label={"Page Description (SEO)"}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: "Page Description (SEO)",
                            }),
                          },
                        ]}
                      >
                        <Input
                          size={"large"}
                          placeholder={t("common.text.input", {
                            item: "Page Description (SEO)",
                          })}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        name="meta_keyword"
                        label={"Keywords (SEO)"}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: "Keywords (SEO)",
                            }),
                          },
                        ]}
                      >
                        <Input
                          size={"large"}
                          placeholder={t("common.text.input", {
                            item: "Keywords (SEO)",
                          })}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        name="title_about"
                        label={"Title About Us"}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: "Title About Us",
                            }),
                          },
                        ]}
                      >
                        <Input
                          size={"large"}
                          placeholder={t("common.text.input", {
                            item: "Title About Us",
                          })}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        name="description_about"
                        label={"Description About Us"}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: "Description About Us",
                            }),
                          },
                        ]}
                      >
                        <VuiEditor
                          disabled={disable.get()}
                          apiKey={env.getTinyApiKey()}
                          init={{
                            ...vuiEditorBaseInit,
                            placeholder: t("common.text.input", {
                              item: "Description About Us",
                            }),
                          }}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        label={
                          <Text>
                            Image About Us{" "}
                            <span className="optional-form-label">
                              (
                              {t("common.text.recommendedSize", {
                                item: IMAGE_RECOMMENDATION_SIZE
                                  .informationalPage.home.aboutUs,
                              })}
                              )
                            </span>
                          </Text>
                        }
                        name="image_about"
                      >
                        <VuiUploadImages isUploadFirst />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        name="button_label_about"
                        label={"Button Label About Us"}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: "Button Label About Us",
                            }),
                          },
                        ]}
                      >
                        <Input
                          size={"large"}
                          placeholder={t("common.text.input", {
                            item: "Button Label About Us",
                          })}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        name="button_link_about"
                        label={"Button Link About Us"}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: "Button Link About Us",
                            }),
                          },
                        ]}
                      >
                        <Input
                          size={"large"}
                          placeholder={t("common.text.input", {
                            item: "Button Link About Us",
                          })}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        name="title_social_media"
                        label={"Title Social Media Wall"}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: "Title Social Media Wall",
                            }),
                          },
                        ]}
                      >
                        <Input
                          size={"large"}
                          placeholder={t("common.text.input", {
                            item: "Title Social Media Wall",
                          })}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        name="title_client"
                        label={"Title Our Awesome Client"}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: "Title Our Awesome Client",
                            }),
                          },
                        ]}
                      >
                        <Input
                          size={"large"}
                          placeholder={t("common.text.input", {
                            item: "Title Our Awesome Client",
                          })}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        name="title_office"
                        label={"Title Our Office"}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: "Title Our Office",
                            }),
                          },
                        ]}
                      >
                        <Input
                          size={"large"}
                          placeholder={t("common.text.input", {
                            item: "Title Our Office",
                          })}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        name="title_merchandise"
                        label={"Title Merchandise"}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: "Title Merchandise",
                            }),
                          },
                        ]}
                      >
                        <Input
                          size={"large"}
                          placeholder={t("common.text.input", {
                            item: "Title Merchandise",
                          })}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <div className="vui-form-group type-column">
                    <div className="input-section">
                      <Form.Item
                        name="button_link_merchandise"
                        label={"Button Link Merchandise"}
                        rules={[
                          {
                            required: true,
                            message: t("validation.required", {
                              item: "Button Link Merchandise",
                            }),
                          },
                        ]}
                      >
                        <Input
                          size={"large"}
                          placeholder={t("common.text.input", {
                            item: "Button Link Merchandise",
                          })}
                        />
                      </Form.Item>
                    </div>
                  </div>

                  <Space
                    style={{
                      width: "100%",
                    }}
                    direction="vertical"
                    size={15}
                  >
                    <Form.List name="banners">
                      {(fields, { add, remove }) => (
                        <Card
                          title={"Add Banners"}
                          extra={
                            <Button
                              type="primary"
                              onClick={() => add()}
                              icon={<PlusOutlined />}
                            >
                              Add
                            </Button>
                          }
                        >
                          <Space
                            style={{
                              width: "100%",
                            }}
                            direction="vertical"
                            size={15}
                          >
                            {fields.map(({ key, name, ...restField }) => (
                              <Card
                                title={`Banner ${name + 1}`}
                                key={key}
                                extra={
                                  <>
                                    {name !== 0 ? (
                                      <DeleteOutlined
                                        onClick={() => remove(name)}
                                      />
                                    ) : (
                                      ""
                                    )}
                                  </>
                                }
                              >
                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "title"]}
                                      label={"Title"}
                                    >
                                      <VuiEditor
                                        disabled={disable.get()}
                                        apiKey={env.getTinyApiKey()}
                                        init={{
                                          ...vuiEditorBaseInit,
                                          height: 140,
                                          placeholder: t("common.text.input", {
                                            item: "Title",
                                          }),
                                        }}
                                      />
                                    </Form.Item>
                                  </div>
                                </div>

                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "description"]}
                                      label={"Description"}
                                    >
                                      <VuiEditor
                                        disabled={disable.get()}
                                        apiKey={env.getTinyApiKey()}
                                        init={{
                                          ...vuiEditorBaseInit,
                                          placeholder: t("common.text.input", {
                                            item: "Description",
                                          }),
                                        }}
                                      />
                                    </Form.Item>
                                  </div>
                                </div>
                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "image"]}
                                      label={
                                        <Text>
                                          Image{" "}
                                          <span className="optional-form-label">
                                            (
                                            {t("common.text.recommendedSize", {
                                              item: IMAGE_RECOMMENDATION_SIZE
                                                .informationalPage.home.banner,
                                            })}
                                            )
                                          </span>
                                        </Text>
                                      }
                                      rules={[
                                        {
                                          required: true,
                                          message: t("validation.required", {
                                            item: "Image",
                                          }),
                                        },
                                      ]}
                                    >
                                      <VuiUploadImages isUploadFirst />
                                    </Form.Item>
                                  </div>
                                </div>
                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "image_mobile"]}
                                      label={
                                        <Text>
                                          Image Mobile{" "}
                                          <span className="optional-form-label">
                                            (
                                            {t("common.text.recommendedSize", {
                                              item: IMAGE_RECOMMENDATION_SIZE
                                                .informationalPage.home
                                                .bannerMobile,
                                            })}
                                            )
                                          </span>
                                        </Text>
                                      }
                                      rules={[
                                        {
                                          required: true,
                                          message: t("validation.required", {
                                            item: "Image Mobile",
                                          }),
                                        },
                                      ]}
                                    >
                                      <VuiUploadImages isUploadFirst />
                                    </Form.Item>
                                  </div>
                                </div>
                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      name={[name, "can_click"]}
                                      label={"Banner Click"}
                                      valuePropName="checked"
                                    >
                                      <Switch disabled={disable.get()} />
                                    </Form.Item>
                                  </div>
                                </div>
                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "button_link"]}
                                      label={"Link Banner"}
                                      rules={[
                                        {
                                          required: true,
                                          message: t("validation.required", {
                                            item: "Link Banner",
                                          }),
                                        },
                                      ]}
                                    >
                                      <Input
                                        size={"large"}
                                        placeholder={t("common.text.input", {
                                          item: "Link Banner",
                                        })}
                                      />
                                    </Form.Item>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </Space>
                        </Card>
                      )}
                    </Form.List>

                    <Form.List name="clients">
                      {(fields, { add, remove }) => (
                        <Card
                          title={"Add Clients"}
                          extra={
                            <Button
                              type="primary"
                              onClick={() => add()}
                              icon={<PlusOutlined />}
                            >
                              Add
                            </Button>
                          }
                        >
                          <Space
                            style={{
                              width: "100%",
                            }}
                            direction="vertical"
                            size={15}
                          >
                            {fields.map(({ key, name, ...restField }) => (
                              <Card
                                title={`Client ${name + 1}`}
                                key={key}
                                extra={
                                  <>
                                    {name !== 0 ? (
                                      <DeleteOutlined
                                        onClick={() => remove(name)}
                                      />
                                    ) : (
                                      ""
                                    )}
                                  </>
                                }
                              >
                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "name"]}
                                      label={"Name"}
                                      rules={[
                                        {
                                          required: true,
                                          message: t("validation.required", {
                                            item: "Name",
                                          }),
                                        },
                                      ]}
                                    >
                                      <Input
                                        size={"large"}
                                        placeholder={t("common.text.input", {
                                          item: "Name",
                                        })}
                                      />
                                    </Form.Item>
                                  </div>
                                </div>

                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "summary"]}
                                      label={"Summary"}
                                    >
                                      <TextArea
                                        rows={3}
                                        size={"large"}
                                        placeholder={t("common.text.input", {
                                          item: "Summary",
                                        })}
                                      />
                                    </Form.Item>
                                  </div>
                                </div>
                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "image"]}
                                      label={
                                        <Text>
                                          Image{" "}
                                          <span className="optional-form-label">
                                            (
                                            {t("common.text.recommendedSize", {
                                              item: IMAGE_RECOMMENDATION_SIZE
                                                .informationalPage.home
                                                .addClients,
                                            })}
                                            )
                                          </span>
                                        </Text>
                                      }
                                      rules={[
                                        {
                                          required: true,
                                          message: t("validation.required", {
                                            item: "Image",
                                          }),
                                        },
                                      ]}
                                    >
                                      <VuiUploadImages isUploadFirst />
                                    </Form.Item>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </Space>
                        </Card>
                      )}
                    </Form.List>

                    <Card title="Social Media Wall">
                      <Space
                        style={{
                          width: "100%",
                        }}
                        direction="vertical"
                        size={15}
                      >
                        <div className="vui-form-group type-column">
                          <div className="input-section">
                            <Form.Item
                              name="instagram_link"
                              label={"Instagram Link"}
                              rules={[
                                {
                                  required: true,
                                  message: t("validation.required", {
                                    item: "Instagram Link",
                                  }),
                                },
                              ]}
                            >
                              <Input
                                size={"large"}
                                placeholder={t("common.text.input", {
                                  item: "Instagram Link",
                                })}
                              />
                            </Form.Item>
                          </div>
                        </div>
                        <div className="vui-form-group type-column">
                          <div className="input-section">
                            <Form.Item
                              name="youtube_link"
                              label={"Youtube Link"}
                              rules={[
                                {
                                  required: true,
                                  message: t("validation.required", {
                                    item: "Youtube Link",
                                  }),
                                },
                              ]}
                            >
                              <Input
                                size={"large"}
                                placeholder={t("common.text.input", {
                                  item: "Youtube Link",
                                })}
                              />
                            </Form.Item>
                          </div>
                        </div>
                        <div className="vui-form-group type-column">
                          <div className="input-section">
                            <Form.Item
                              name="linkedin_link"
                              label={"Linkedin Link"}
                              rules={[
                                {
                                  required: true,
                                  message: t("validation.required", {
                                    item: "Linkedin Link",
                                  }),
                                },
                              ]}
                            >
                              <Input
                                size={"large"}
                                placeholder={t("common.text.input", {
                                  item: "Linkedin Link",
                                })}
                              />
                            </Form.Item>
                          </div>
                        </div>
                      </Space>
                    </Card>

                    <Form.List name="offices">
                      {(fields, { add, remove }) => (
                        <Card
                          title={"Add Our Office"}
                          extra={
                            <Button
                              type="primary"
                              onClick={() => add()}
                              icon={<PlusOutlined />}
                            >
                              Add
                            </Button>
                          }
                        >
                          <Space
                            style={{
                              width: "100%",
                            }}
                            direction="vertical"
                            size={15}
                          >
                            {fields.map(({ key, name, ...restField }) => (
                              <Card
                                title={`Our Office ${name + 1}`}
                                key={key}
                                extra={
                                  <>
                                    {name !== 0 ? (
                                      <DeleteOutlined
                                        onClick={() => remove(name)}
                                      />
                                    ) : (
                                      ""
                                    )}
                                  </>
                                }
                              >
                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "description"]}
                                      label={"Description"}
                                      rules={[
                                        {
                                          required: true,
                                          message: t("validation.required", {
                                            item: "Description",
                                          }),
                                        },
                                      ]}
                                    >
                                      <VuiEditor
                                        disabled={disable.get()}
                                        apiKey={env.getTinyApiKey()}
                                        init={{
                                          ...vuiEditorBaseInit,
                                          placeholder: t("common.text.input", {
                                            item: "Description",
                                          }),
                                        }}
                                      />
                                    </Form.Item>
                                  </div>
                                </div>
                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "image"]}
                                      label={
                                        <Text>
                                          Image{" "}
                                          <span className="optional-form-label">
                                            (
                                            {t("common.text.recommendedSize", {
                                              item: IMAGE_RECOMMENDATION_SIZE
                                                .informationalPage.home
                                                .ourOffice,
                                            })}
                                            )
                                          </span>
                                        </Text>
                                      }
                                      rules={[
                                        {
                                          required: true,
                                          message: t("validation.required", {
                                            item: "Image",
                                          }),
                                        },
                                      ]}
                                    >
                                      <VuiUploadImages isUploadFirst />
                                    </Form.Item>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </Space>
                        </Card>
                      )}
                    </Form.List>

                    <Form.List name="merchandises">
                      {(fields, { add, remove }) => (
                        <Card
                          title={"Add Merchandises"}
                          extra={
                            <Button
                              type="primary"
                              onClick={() => add()}
                              icon={<PlusOutlined />}
                            >
                              Add
                            </Button>
                          }
                        >
                          <Space
                            style={{
                              width: "100%",
                            }}
                            direction="vertical"
                            size={15}
                          >
                            {fields.map(({ key, name, ...restField }) => (
                              <Card
                                title={`Merchandise ${name + 1}`}
                                key={key}
                                extra={
                                  <>
                                    {name !== 0 ? (
                                      <DeleteOutlined
                                        onClick={() => remove(name)}
                                      />
                                    ) : (
                                      ""
                                    )}
                                  </>
                                }
                              >
                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "name"]}
                                      label={"Name"}
                                      rules={[
                                        {
                                          required: true,
                                          message: t("validation.required", {
                                            item: "Name",
                                          }),
                                        },
                                      ]}
                                    >
                                      <Input
                                        size={"large"}
                                        placeholder={t("common.text.input", {
                                          item: "Name",
                                        })}
                                      />
                                    </Form.Item>
                                  </div>
                                </div>

                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "description"]}
                                      label={"Description"}
                                      rules={[
                                        {
                                          required: true,
                                          message: t("validation.required", {
                                            item: "Description",
                                          }),
                                        },
                                      ]}
                                    >
                                      <VuiEditor
                                        disabled={disable.get()}
                                        apiKey={env.getTinyApiKey()}
                                        init={{
                                          ...vuiEditorBaseInit,
                                          placeholder: t("common.text.input", {
                                            item: "Description",
                                          }),
                                        }}
                                      />
                                    </Form.Item>
                                  </div>
                                </div>
                                <div className="vui-form-group type-column">
                                  <div className="input-section">
                                    <Form.Item
                                      {...restField}
                                      name={[name, "image"]}
                                      label={
                                        <Text>
                                          Image{" "}
                                          <span className="optional-form-label">
                                            (
                                            {t("common.text.recommendedSize", {
                                              item: IMAGE_RECOMMENDATION_SIZE
                                                .informationalPage.home
                                                .merchandise,
                                            })}
                                            )
                                          </span>
                                        </Text>
                                      }
                                      rules={[
                                        {
                                          required: true,
                                          message: t("validation.required", {
                                            item: "Image",
                                          }),
                                        },
                                      ]}
                                    >
                                      <VuiUploadImages isUploadFirst />
                                    </Form.Item>
                                  </div>
                                </div>
                              </Card>
                            ))}
                          </Space>
                        </Card>
                      )}
                    </Form.List>
                  </Space>
                </Space>
              </Card>
            </Col>
          </Row>

          <div className="vui-form-btn-group">
            <Button
              className="vui-btn"
              size={"large"}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
            <Accessible access={ACCESS_PERMISSION.page.home}>
              <Spin spinning={loading.get()}>
                <Button
                  className="vui-btn"
                  type="primary"
                  htmlType="submit"
                  size={"large"}
                >
                  Submit
                </Button>
              </Spin>
            </Accessible>
          </div>
        </Form>
      </VuiContainer>
    </>
  );
};

export default AppPageHomeForm;
